import Board from "components/Common/Board/Board";
import SearchEmpty from "components/Common/Empty/SearchEmpty";
import { Table } from "components/Common/Table/Table";
import Pagination from "components/foundation/Pagination";
import React, { useState } from "react";
import styled from "styled-components";
import { match } from "ts-pattern";
import NoticeDashboardTableRow from "./NoticeDashboardTableRow";
import { useFormContext } from "react-hook-form";
import { NoticeDashboardFormData } from "..";
import useNoticesAutoCompleteQuery from "features/notice/hooks/queries/useNoticesAutoCompleteQuery";
import { PageResponseNoticeDto } from "apis/data-contracts";
import TableInputMobile, {
  TableInputMobileButton,
} from "components/Common/Table/TableInputMobile";
import { SearchIcon, DateIcon, ReloadIcon } from "assets/icons";
import TableDatePickerMobile from "components/Common/Table/TableDatePickerMobile";

interface NoticeDashboardWebPageProps {
  data: PageResponseNoticeDto;
  page: number;
  setSearchOptions: React.Dispatch<
    React.SetStateAction<{
      searchType: string;
      search: string;
      startDate: string | undefined;
      endDate: string | undefined;
      page: number;
    }>
  >;
}

const NoticeDashboardMobilePage = ({
  data,
  page,
  setSearchOptions,
}: NoticeDashboardWebPageProps) => {
  const { watch, setValue } = useFormContext<NoticeDashboardFormData>();

  const [type, keyword] = [watch("searchType"), watch("search")];

  const { data: autoCompleteData } = useNoticesAutoCompleteQuery({
    type,
    keyword,
  });

  const [isReset, setIsReset] = useState<boolean>();

  return (
    <NoticeDashboardWebPageLayout>
      <ContentBoard>
        <Table.Header>
          <Table.TotalCount>총 {data.totalElements}건</Table.TotalCount>

          <div
            style={{
              display: "flex",
              gap: "1rem",
            }}
          >
            <TableInputMobile
              searchType={[
                {
                  label: "제목",
                  value: "title",
                },
                {
                  label: "작성자",
                  value: "author",
                },
              ]}
              autocomplete={autoCompleteData?.data.result.results.map((i) => ({
                label: i,
                value: i,
              }))}
            >
              <SearchIcon />
              검색
            </TableInputMobile>
            <TableDatePickerMobile
              onDateRangeSelected={(d) => {
                setSearchOptions((pre) => ({
                  ...pre,
                  startDate: d.start.toString(),
                  endDate: d.end?.toString(),
                }));
              }}
              onClickReset={isReset}
            >
              <DateIcon />
              등록일
            </TableDatePickerMobile>
            <TableInputMobileButton
              variant="tertiary"
              size="small"
              onClick={() => {
                setSearchOptions((pre) => ({
                  ...pre,
                  search: "",
                  startDate: "",
                  endDate: "",
                  page: 1,
                }));

                setValue("search", "");
                setValue("searchType", "title");

                setIsReset(!isReset);
              }}
            >
              <ReloadIcon />
              초기화
            </TableInputMobileButton>
          </div>
        </Table.Header>

        {match(data.totalElements)
          .with(0, () => <SearchEmpty text="등록된 공지사항이 없습니다." />)
          .otherwise(() => (
            <>
              <Table.Layout>
                <Table.Table columns="auto auto" width="100%">
                  <Table.Thead>
                    <Table.Th>제목</Table.Th>
                    <Table.Th>등록일</Table.Th>
                  </Table.Thead>
                  <Table.Tbody>
                    {data.contents.map((notice) => (
                      <NoticeDashboardTableRow
                        id={notice.uuid}
                        key={notice.uuid}
                        title={notice.title}
                        createdAt={notice.createdAt}
                      />
                    ))}
                  </Table.Tbody>
                </Table.Table>
              </Table.Layout>
              <Pagination
                currentPage={page}
                onClickPageButton={(page) =>
                  setSearchOptions((pre) => ({ ...pre, page: page }))
                }
                lastPage={data.totalPages}
                style={{
                  margin: "auto",
                }}
              />
            </>
          ))}
      </ContentBoard>
    </NoticeDashboardWebPageLayout>
  );
};

export default NoticeDashboardMobilePage;

const NoticeDashboardWebPageLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const ContentBoard = styled(Board)`
  padding: 2rem 1.6rem;
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
`;
